import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import VisibilityIcon from "@mui/icons-material/Visibility"
import { useTranslation } from "react-i18next"
import { SharedFilterValues } from "./SharedFilters"
import ExportButton from "./ExportButton"
import DocumentPreviewDialog from "./DocumentPreviewDialog"
import reportsService from "../../services/reportsService"
import * as documentsService from "../../services/documentsService"
import { formatCentsWithSymbol } from "../../utils/formatCurrency"

interface SalesJournalTabProps {
    filters: SharedFilterValues
    companyId: string
}

const fmtC = (v: number, c: string = "EUR") => formatCentsWithSymbol(v, c)
const fmtDate = (d: string) =>
    d
        ? new Date(d).toLocaleDateString("fr-FR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "—"

const SaleRow: React.FC<{ entry: any; companyId: string }> = ({
    entry,
    companyId,
}) => {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewHtml, setPreviewHtml] = useState<string | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewDocNumber, setPreviewDocNumber] = useState("")

    const hasPayments = entry.payments && entry.payments.length > 0
    const receipts: any[] = entry.receipts || []

    const handleReceiptPreview = async (receipt: any) => {
        setPreviewDocNumber(
            receipt.number || String(t("exportsComponents.common.unnumbered"))
        )
        setPreviewOpen(true)
        setPreviewLoading(true)
        setPreviewHtml(null)
        try {
            const html = await documentsService.getHtmlPreview(
                companyId,
                receipt.id
            )
            setPreviewHtml(html)
        } catch (err) {
            console.error("Failed to load receipt preview:", err)
            setPreviewHtml(null)
        } finally {
            setPreviewLoading(false)
        }
    }

    return (
        <>
            <TableRow hover>
                <TableCell sx={{ width: 40, p: 0.5 }}>
                    {hasPayments && (
                        <IconButton size="small" onClick={() => setOpen(!open)}>
                            {open ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                            )}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>{entry.sale_number || "—"}</TableCell>
                <TableCell>{fmtDate(entry.sale_date)}</TableCell>
                <TableCell>{entry.outlet_name || "—"}</TableCell>
                <TableCell>{entry.customer_name || "—"}</TableCell>
                <TableCell>{entry.cashier_name || "—"}</TableCell>
                <TableCell align="right">
                    {fmtC(entry.net_total || 0, entry.currency)}
                </TableCell>
                <TableCell align="right">
                    {fmtC(entry.vat_total || 0, entry.currency)}
                </TableCell>
                <TableCell align="right">
                    {fmtC(entry.gross_total || 0, entry.currency)}
                </TableCell>
                <TableCell align="center">
                    {receipts.length === 0 ? (
                        <Typography variant="caption" color="text.disabled">
                            —
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                gap: 0.5,
                                justifyContent: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            {receipts.map((r: any) => (
                                <Tooltip
                                    key={r.id}
                                    title={
                                        r.number ||
                                        String(
                                            t(
                                                "exportsComponents.salesJournalTab.receiptTooltip"
                                            )
                                        )
                                    }
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => handleReceiptPreview(r)}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ))}
                        </Box>
                    )}
                </TableCell>
            </TableRow>
            {hasPayments && (
                <TableRow>
                    <TableCell
                        colSpan={10}
                        sx={{ py: 0, borderBottom: open ? undefined : "none" }}
                    >
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ mx: 4, my: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mb: 0.5, display: "block" }}
                                >
                                    {t(
                                        "exportsComponents.salesJournalTab.payments.title",
                                        {
                                            count: entry.payments.length,
                                        }
                                    )}
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {t(
                                                    "exportsComponents.salesJournalTab.payments.columns.paymentMethod"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {t(
                                                    "exportsComponents.salesJournalTab.payments.columns.type"
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {t(
                                                    "exportsComponents.salesJournalTab.payments.columns.amount"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {t(
                                                    "exportsComponents.salesJournalTab.payments.columns.date"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {t(
                                                    "exportsComponents.salesJournalTab.payments.columns.recordedBy"
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {entry.payments.map(
                                            (p: any, i: number) => (
                                                <TableRow key={i}>
                                                    <TableCell>
                                                        {p.payment_method_name ||
                                                            "—"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {p.payment_method_type ||
                                                            "—"}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {fmtC(
                                                            p.amount_minor || 0,
                                                            p.currency
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {fmtDate(p.paid_at)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {p.recorded_by || "—"}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
            <DocumentPreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                html={previewHtml}
                loading={previewLoading}
                documentNumber={previewDocNumber}
                documentType="receipt"
            />
        </>
    )
}

const SalesJournalTab: React.FC<SalesJournalTabProps> = ({
    filters,
    companyId,
}) => {
    const { t } = useTranslation()
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    const loadData = useCallback(async () => {
        if (!companyId) return
        setLoading(true)
        try {
            const result = await reportsService.getSalesJournal(companyId, {
                outletId: filters.outletId || undefined,
                registerId: filters.registerId || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
            })
            setData(result)
        } catch (err) {
            console.error("Failed to load sales journal:", err)
        } finally {
            setLoading(false)
        }
    }, [companyId, filters])

    useEffect(() => {
        loadData()
    }, [loadData])

    useEffect(() => {
        setPage(0)
    }, [filters])

    const entries = data?.entries || []
    const totals = data?.totals || {}
    const total = data?.total || 0

    return (
        <Box>
            {loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : entries.length > 0 ? (
                <>
                    <Paper
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            gap: 3,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.salesJournalTab.summary.totalSales"
                                )}
                                :
                            </strong>{" "}
                            {total}
                        </Typography>
                        <Typography variant="body2">
                            <strong>{t("exportsComponents.common.ht")}:</strong>{" "}
                            {fmtC(totals.net_total || 0)}
                        </Typography>
                        <Typography variant="body2">
                            <strong>
                                {t("exportsComponents.common.vat")}:
                            </strong>{" "}
                            {fmtC(totals.vat_total || 0)}
                        </Typography>
                        <Typography variant="body2">
                            <strong>
                                {t("exportsComponents.common.ttc")}:
                            </strong>{" "}
                            {fmtC(totals.gross_total || 0)}
                        </Typography>
                        <Box sx={{ ml: "auto" }}>
                            <ExportButton
                                companyId={companyId}
                                reportType="sales_journal"
                                filters={filters}
                            />
                        </Box>
                    </Paper>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 40 }} />
                                    <TableCell>
                                        {t(
                                            "exportsComponents.salesJournalTab.columns.saleNumber"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.salesJournalTab.columns.date"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.salesJournalTab.columns.outlet"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.salesJournalTab.columns.customer"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.salesJournalTab.columns.cashier"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.ht")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.vat")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.ttc")}
                                    </TableCell>
                                    <TableCell align="center">
                                        {t(
                                            "exportsComponents.salesJournalTab.columns.receipts"
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entries
                                    .slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    )
                                    .map((entry: any) => (
                                        <SaleRow
                                            key={entry.id}
                                            entry={entry}
                                            companyId={companyId}
                                        />
                                    ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={entries.length}
                            page={page}
                            onPageChange={(_e, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10))
                                setPage(0)
                            }}
                            rowsPerPageOptions={[25, 50, 100]}
                            labelRowsPerPage={String(t("common.rowsPerPage"))}
                            labelDisplayedRows={({ from, to, count }) =>
                                String(
                                    t("common.paginationDisplayedRows", {
                                        from,
                                        to,
                                        count: count === -1 ? to : count,
                                    })
                                )
                            }
                        />
                    </TableContainer>
                </>
            ) : (
                <Paper sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                        {t("exportsComponents.salesJournalTab.empty")}
                    </Typography>
                </Paper>
            )}
        </Box>
    )
}

export default SalesJournalTab
